import { createFileRoute, Link } from "@tanstack/react-router";
import { Home, Calendar, Star, FileSignature, ArrowRight, Key, Users, MapPin } from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import { alternateLinkList } from "@/lib/i18n/locale";
import hero from "@/assets/nfc-card-lifestyle.jpg";
import booking from "@/assets/phone-card.jpg";
import reviews from "@/assets/ai-abstract.jpg";
import nurture from "@/assets/cards-sharing.webp";

export const Route = createFileRoute("/el/solutions/real-estate")({
  head: () => ({
    meta: [
      { title: "Κάρτες NFC & CRM για μεσίτες ακινήτων — banana.cards" },
      { name: "description", content: "Μοιραστείτε αγγελίες με ένα άγγιγμα, κλείστε επισκέψεις επιτόπου, αυτοματοποιήστε τα αιτήματα αξιολόγησης και μείνετε στο μυαλό κάθε παλιού πελάτη για χρόνια." },
      { property: "og:title", content: "Κάρτες NFC & CRM για μεσίτες ακινήτων — banana.cards" },
      { property: "og:description", content: "Αγγελίες, κρατήσεις, συμβάσεις και αξιολογήσεις — σε μια κάρτα που μοιράζεστε με ένα άγγιγμα." },
      { property: "og:image", content: hero },
      { property: "og:locale", content: "el_GR" },
    ],
    links: alternateLinkList("/solutions/real-estate", "el"),
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="Για επαγγελματίες ακινήτων"
      title="Μείνετε η πρώτη τους επιλογή —"
      italic="για χρόνια."
      description="Αγγίξτε την κάρτα σας σε μια παρουσίαση ακινήτου, κλείστε επιτόπου τη δεύτερη επίσκεψη, στείλτε τη σύμβαση με ένα κλικ και αφήστε το αίτημα αξιολόγησης πέντε αστέρων να σταλεί την επομένη της ολοκλήρωσης — αυτόματα."
      heroImage={hero}
      metrics={[
        { v: "4,9★", l: "Μέση βαθμολογία αξιολογήσεων μεσιτών" },
        { v: "+62%", l: "Περισσότερες αναθέσεις από συστάσεις" },
        { v: "7 χρόνια", l: "Μέσος κύκλος διατήρησης σχέσης με πελάτες" },
      ]}
      bullets={[
        { icon: Calendar, title: "Κρατήσεις πάνω στην κάρτα σας", desc: "Οι αγοραστές αγγίζουν, επιλέγουν ώρα επίσκεψης και πληρώνουν προκαταβολή — χωρίς να φύγουν από το εταιρικό σας προφίλ." },
        { icon: Star, title: "Αυτοματοποίηση αξιολογήσεων", desc: "Μετά από κάθε ολοκλήρωση συναλλαγής, το Banana στέλνει το κατάλληλο αίτημα αξιολόγησης (Google, Zillow, Realtor), την κατάλληλη στιγμή, με το δικό σας ύφος." },
        { icon: FileSignature, title: "Συμβάσεις & γνωστοποιήσεις, υπογεγραμμένες", desc: "Συμφωνίες ανάθεσης, εκπροσώπηση αγοραστή, γνωστοποιήσεις — υπογράφονται ηλεκτρονικά στο Banana και αποθηκεύονται με ασφάλεια για τις ανάγκες συμμόρφωσης." },
      ]}
      deepDives={[
        {
          eyebrow: "Πρώτη επαφή",
          title: "Από την παρουσίαση ακινήτου",
          italic: "στη δεύτερη επίσκεψη — σε 30 δευτερόλεπτα.",
          description: "Αγγίξτε με την κάρτα NFC σας το τηλέφωνο του επισκέπτη. Βλέπει τις αγγελίες, τις αξιολογήσεις και το ζωντανό ημερολόγιό σας. Κλείνει την επόμενη επίσκεψη πριν φύγει από το ακίνητο.",
          points: [
            "Καρουζέλ επιλεγμένων αγγελιών στο προφίλ",
            "Ζωντανό ημερολόγιο με διαθέσιμες ώρες",
            "Προκαταβολές Stripe για σοβαρούς αγοραστές",
            "Αυτόματη προσθήκη στο pipeline διατήρησης σχέσεων",
          ],
          image: booking,
        },
        {
          eyebrow: "Φήμη",
          title: "Γίνετε ο μεσίτης με",
          italic: "100+ αξιολογήσεις πέντε αστέρων.",
          description: "Κάθε ολοκλήρωση ενεργοποιεί μια ακολουθία: SMS την επόμενη ημέρα για Google, email την τρίτη ημέρα για Zillow, διακριτική υπενθύμιση τη 14η αν δεν υπάρχει απάντηση. Ξυπνάτε με νέες αξιολογήσεις χωρίς καμία προσπάθεια.",
          points: [
            "Δρομολόγηση αξιολογήσεων σε πολλές πλατφόρμες",
            "Το AI συντάσσει ξανά την απάντησή σας στο ύφος σας",
            "Αναχαίτιση αρνητικών σχολίων — φτάνουν πρώτα σε εσάς",
            "Ενσωματώσιμα widget για τις σελίδες αγγελιών",
          ],
          image: reviews,
          reverse: true,
        },
        {
          eyebrow: "Διατήρηση σχέσεων",
          title: "Μείνετε στη ζωή τους",
          italic: "για τα επόμενα 7 χρόνια.",
          description: "Οι περισσότεροι παλιοί πελάτες μετακομίζουν κάθε 5–7 χρόνια. Το Banana στέλνει ευχές για την επέτειο αγοράς, ενημερώσεις της τοπικής αγοράς και ευχές γενεθλίων — ώστε όταν αποφασίσουν να πουλήσουν, να καλέσουν μόνο εσάς.",
          points: [
            "Αυτοματισμοί επετείων & γενεθλίων",
            "Τριμηνιαίες αναφορές της τοπικής αγοράς",
            "Ακολουθίες αιτημάτων συστάσεων",
            "Ετικέτες & τμηματοποίηση παλιών πελατών",
          ],
          image: nurture,
        },
      ]}
      howItWorks={[
        { title: "Παραγγείλετε την κάρτα σας", desc: "Μεταλλική ή PVC, με λογότυπο, φωτογραφία και μεσιτικό γραφείο. Αποστολή σε 5–7 ημέρες." },
        { title: "Συνδέστε αγγελίες & ημερολόγιο", desc: "Συγχρονίστε τη ροή MLS, το ημερολόγιο Google/Outlook και τον λογαριασμό Stripe για προκαταβολές." },
        { title: "Ένα άγγιγμα σε κάθε επίσκεψη", desc: "Καταγράψτε τον υποψήφιο πελάτη, κλείστε επόμενο ραντεβού και ξεκινήστε το pipeline διατήρησης σχέσεων — με ένα άγγιγμα." },
        { title: "Ολοκληρώστε & αφήστε το να δουλέψει", desc: "Ηλεκτρονική υπογραφή συμβάσεων στο Banana. Αυτόματη ενεργοποίηση αξιολογήσεων. Ξεκινά η επταετής διατήρηση σχέσεων." },
      ]}
      capabilities={[
        "Επαγγελματικές κάρτες NFC με εταιρική ταυτότητα",
        "Καρουζέλ επιλεγμένων αγγελιών",
        "Κρατήσεις & συγχρονισμός ημερολογίου",
        "Είσπραξη προκαταβολών με Stripe",
        "Ηλεκτρονική υπογραφή συμφωνιών ανάθεσης & αγοράς",
        "Αυτοματοποίηση αξιολογήσεων σε πολλές πλατφόρμες",
        "Ακολουθίες επικοινωνίας με παλιούς πελάτες",
        "Ενσωματώσεις MLS & Zillow",
      ]}
      faq={[
        { q: "Λειτουργεί με το MLS και τον λογαριασμό Zillow μου;", a: "Ναι. Το Banana αντλεί τις ενεργές αγγελίες σας από τις περισσότερες μεγάλες ροές MLS και το Zillow Premier Agent. Εμφανίζονται αυτόματα στο προφίλ σας και ενημερώνονται σε πραγματικό χρόνο όταν αλλάζει η κατάσταση: ενεργό → σε εκκρεμότητα → πωλήθηκε." },
        { q: "Μπορούν οι αγοραστές να κλείσουν επίσκεψη απευθείας από την κάρτα μου;", a: "Ναι. Με ένα άγγιγμα στην κάρτα σας βλέπουν το ζωντανό ημερολόγιο Google, Outlook ή iCloud με τις ώρες που έχετε διαθέσει για επισκέψεις. Επιλέγουν ώρα, πληρώνουν προαιρετικά προκαταβολή μέσω Stripe και το ραντεβού καταχωρίζεται με όλα τα στοιχεία επικοινωνίας τους." },
        { q: "Πώς λειτουργούν οι συμβάσεις και οι γνωστοποιήσεις;", a: "Το Banana διαθέτει μηχανισμό ηλεκτρονικής υπογραφής συμβατό με ESIGN, UETA και eIDAS. Χρησιμοποιήστε τα πρότυπά μας για συμφωνίες ανάθεσης, εκπροσώπησης αγοραστή και γνωστοποιήσεις ή ανεβάστε τα δικά σας. Οι πελάτες υπογράφουν στο τηλέφωνό τους σε λιγότερο από ένα λεπτό. Τα υπογεγραμμένα PDF αποθηκεύονται με ιστορικό ελέγχου." },
        { q: "Τι στέλνει η αυτοματοποίηση αξιολογήσεων;", a: "Μόλις σημειώσετε μια συμφωνία ως ολοκληρωμένη, ξεκινά η ακολουθία: ημέρα 1 — φιλικό ευχαριστήριο SMS με σύνδεσμο αξιολόγησης Google· ημέρα 3 — email με σύνδεσμο Zillow αν δεν υπάρχει ακόμη αξιολόγηση Google· ημέρα 14 — διακριτική υπενθύμιση. Το AI προσαρμόζει κάθε μήνυμα στο ύφος σας. Οι αρνητικές απαντήσεις αναχαιτίζονται και φτάνουν πρώτα σε εσάς, ποτέ δημόσια." },
        { q: "Πόσο διαρκεί η επικοινωνία με παλιούς πελάτες;", a: "Έως 10 χρόνια, αυτόματα. Επέτειοι, γενέθλια, τριμηνιαίες ενημερώσεις της τοπικής αγοράς και εποχική επικοινωνία — όλα με το ύφος σας. Οι περισσότεροι μεσίτες βλέπουν αύξηση συστάσεων και επαναλαμβανόμενων συνεργασιών μέσα στους πρώτους 18 μήνες." },
      ]}
      outro={{
        title: "Γίνετε ο μεσίτης",
        italic: "που θυμούνται.",
        description: "Κάθε άγγιγμα είναι μια μελλοντική ανάθεση. Κάθε αξιολόγηση ένας μελλοντικός πελάτης. Κάθε σχέση μια σύσταση που περιμένει να γίνει.",
      }}
      afterFaq={<REUseCases />}
    />
  );
}

function REUseCases() {
  const items = [
    { i: Home, t: "Μεσίτες πωλητών", d: "Μοιραστείτε αγγελίες με άγγιγμα, καταγράψτε το ενδιαφέρον αγοραστών και κλείστε επισκέψεις χωρίς αλλεπάλληλα τηλεφωνήματα.", to: "/el/platform/bookings" },
    { i: Users, t: "Μεσίτες αγοραστών", d: "Αξιολογήστε το ενδιαφέρον επιτόπου, στείλτε γνωστοποιήσεις και υπογράψτε ηλεκτρονικά τη συμφωνία εκπροσώπησης ακόμη και στο πάρκινγκ.", to: "/el/platform/contracts" },
    { i: Key, t: "Διαχειριστές ακινήτων", d: "Κωδικοί QR σε κάθε ακίνητο και αυτόματη δρομολόγηση αιτημάτων συντήρησης.", to: "/platform/networking/qr-code" },
    { i: MapPin, t: "Μεσιτικά γραφεία & ομάδες", d: "Κάρτες με εταιρική ταυτότητα για όλους τους μεσίτες, με κεντρικές αναφορές & SSO.", to: "/el/solutions/enterprise" },
  ];
  return (
    <section className="bg-background">
      <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Εφαρμογές στον κλάδο ακινήτων</p>
        <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
          Κάθε ρόλος στο μεσιτικό γραφείο, <em className="italic">με πλήρη κάλυψη.</em>
        </h2>
        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {items.map((i) => {
            const Icon = i.i;
            return (
              <Link key={i.t} to={i.to} className="group rounded-2xl bg-muted p-6 transition hover:bg-muted/70">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-soft">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-lg font-bold">{i.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{i.d}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold underline-offset-4 group-hover:underline">
                  Εξερευνήστε <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}